<template>
  <v-theme-provider dark>
    <section id="hero-alt">
      <base-img
        :height="heroHeight"
        :src="src"
        color="#45516b"
        flat
        max-width="100%"
        tile
      >
        <div
          v-if="titleText || subtitle"
          class="hero-alt-content"
          :style="contentStyle"
        >
          <h1
            v-if="titleText"
            class="hero-alt-title"
            :style="titleStyle"
          >
            {{ titleText }}
          </h1>
          <p
            v-if="subtitle"
            class="hero-alt-subtitle"
            :style="subtitleStyle"
          >
            {{ subtitle }}
          </p>
        </div>
      </base-img>
    </section>
  </v-theme-provider>
</template>

<script>
  export default {
    name: 'SectionHeroAlt',

    metaInfo() {
      return {
        changed: (meta) => {
          // tab 切换导致子页面 metaInfo 更新时，不覆盖已设置的分类标题
          if (this._tabTitleActive) return
          const i18nKey = this.$route?.meta?.i18n
          this.title = i18nKey ? this.$t(i18nKey) : ''
        },
      }
    },

    data: () => ({
      title: '',
      heroBgUrl: null,
      heroMobileBgUrl: null,
      customHeight: null,
      customMobileHeight: null,
      heroConfig: {},
    }),
    computed: {
      src() {
        if (this.isMobile && this.heroMobileBgUrl) {
          return this.heroMobileBgUrl
        }
        return this.heroBgUrl || ''
      },
      heroHeight() {
        if (this.isMobile && this.customMobileHeight) {
          if (/^\d+$/.test(String(this.customMobileHeight))) {
            return Number(this.customMobileHeight)
          }
          return this.customMobileHeight
        }
        if (this.customHeight) {
          if (/^\d+$/.test(String(this.customHeight))) {
            return Number(this.customHeight)
          }
          return this.customHeight
        }
        return this.$vuetify.breakpoint.mdAndUp ? 600 : 400
      },
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown
      },
      contentStyle() {
        const style = {}
        const cfg = this.heroConfig
        const align = cfg.content_align
        const offsetX = this.isMobile && cfg.mobile_content_offset_x
          ? cfg.mobile_content_offset_x
          : cfg.content_offset_x
        const offsetY = this.isMobile && cfg.mobile_content_offset_y
          ? cfg.mobile_content_offset_y
          : cfg.content_offset_y

        if (align === 'left') {
          style.left = offsetX || (this.isMobile ? '6vw' : '10vw')
          style.textAlign = 'left'
        } else if (align === 'right') {
          style.left = 'auto'
          style.right = offsetX || (this.isMobile ? '6vw' : '10vw')
          style.textAlign = 'right'
        } else {
          // center (default)
          style.left = '50%'
          style.transform = 'translate(-50%, -50%)'
          style.textAlign = 'center'
          if (offsetX) {
            style.marginLeft = offsetX
          }
        }

        if (offsetY) {
          if (align === 'center' || !align) {
            style.transform = `translate(-50%, calc(-50% + ${offsetY}))`
          } else {
            style.transform = `translateY(calc(-50% + ${offsetY}))`
          }
        }

        return style
      },
      titleStyle() {
        const style = {}
        const cfg = this.heroConfig
        const titleColor = (this.isMobile && cfg.mobile_title_color) ? cfg.mobile_title_color : cfg.title_color
        if (titleColor) style.color = titleColor
        const fs = this.isMobile ? cfg.mobile_title_font_size : cfg.title_font_size
        if (fs) style.fontSize = fs
        if (cfg.title_font_weight) style.fontWeight = cfg.title_font_weight
        const w = this.isMobile ? cfg.mobile_title_width : cfg.title_width
        if (w) style.maxWidth = w
        return style
      },
      // 标题文本：heroConfig 配置的多语言标题优先，否则使用路由 i18n 标题
      titleText() {
        const cfg = this.heroConfig
        const locale = (this.$i18n?.locale || 'en').split('-')[0]
        const localeKey = `title_${locale}`
        return cfg[localeKey] || this.title
      },
      // 副标题文本：根据当前语言取 subtitle_zh / subtitle_en，若无则取通用 subtitle
      subtitle() {
        const cfg = this.heroConfig
        const locale = (this.$i18n?.locale || 'en').split('-')[0]
        const localeKey = `subtitle_${locale}`
        const text = cfg[localeKey] || cfg.subtitle || ''
        return text
      },
      subtitleStyle() {
        const style = {}
        const cfg = this.heroConfig
        const color = (this.isMobile && cfg.mobile_subtitle_color) ? cfg.mobile_subtitle_color : cfg.subtitle_color
        if (color) style.color = color
        const fs = this.isMobile ? cfg.mobile_subtitle_font_size : cfg.subtitle_font_size
        if (fs) style.fontSize = fs
        if (cfg.subtitle_font_weight) style.fontWeight = cfg.subtitle_font_weight
        const w = this.isMobile ? cfg.mobile_subtitle_width : cfg.subtitle_width
        if (w) style.maxWidth = w
        return style
      },
    },
    watch: {
      '$i18n.locale'(newValue) {
        // 语言切换时清除 tab 覆盖标记，让 Gallery 重新加载后重设
        this._tabTitleActive = false
        const { i18n } = this.$route.meta
        this.title = this.$t(i18n)
        // heroConfig 的标题覆盖由 titleText computed 自动响应语言切换，无需额外处理
      },
    },

    async mounted() {
      await this.loadHeroBg()
      // 监听 Gallery tab 切换事件（products / solutions 共用）
      this._galleryTabHandler = ({ label, isAll }) => {
        this.title = label
        this._tabTitleActive = !isAll
      }
      this.$root.$on('gallery-tab-changed', this._galleryTabHandler)
    },

    beforeDestroy() {
      if (this._galleryTabHandler) {
        this.$root.$off('gallery-tab-changed', this._galleryTabHandler)
      }
    },

    methods: {
      async loadHeroBg() {
        const routeName = (this.$route?.name || '').toLowerCase()
        const nameMap = {
          about: 'about',
          products: 'products',
          solutions: 'solutions',
          blog: 'blog',
          blogcategory: 'blog',
          blogtag: 'blog',
          partnership: 'partnership',
          contact: 'contact',
        }
        const pageKey = nameMap[routeName]
        if (!pageKey) return
        try {
          const resp = await this.$axios({
            method: 'get',
            url: '/public/page-hero',
            params: { pages: pageKey },
          })
          const item = resp?.[pageKey]
          if (item) {
            const url = typeof item === 'string' ? item : item.url
            const mobileUrl = typeof item === 'object' ? item.mobile_url : null
            const height = typeof item === 'string' ? null : item.height
            if (url && url.trim()) {
              this.heroBgUrl = url.trim()
            }
            if (mobileUrl && mobileUrl.trim()) {
              this.heroMobileBgUrl = mobileUrl.trim()
            }
            if (height && String(height).trim()) {
              this.customHeight = String(height).trim()
            }
            const mobileHeight = typeof item === 'string' ? null : item.mobile_height
            if (mobileHeight && String(mobileHeight).trim()) {
              this.customMobileHeight = String(mobileHeight).trim()
            }
            if (typeof item === 'object') {
              this.heroConfig = item
            }
          }
        } catch {
          // fallback 到默认，不做任何处理
        }
      },
    },
  }
</script>

<style lang="sass">
#hero-alt
  .v-image
    position: relative
    transition: height 0.35s ease !important

  .hero-alt-content
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    width: 90%
    max-width: 900px

  .hero-alt-title
    font-size: 2.2rem
    font-weight: 600
    color: #fff
    padding: 20px 0
    margin: 0

  .hero-alt-subtitle
    font-size: 1.1rem
    font-weight: 400
    color: rgba(255, 255, 255, 0.85)
    margin: 0
    padding: 0
    line-height: 1.6

  @media screen and (max-width: 960px)
    .hero-alt-title
      font-size: 1.4rem

    .hero-alt-subtitle
      font-size: 0.95rem
</style>
